import {useGTMDispatch} from './react-gtm-hook/index'
import {useDataCloudDispatch} from './data-cloud'
import {
  transformSalesforceBasketItemsForGA4,
  transformAlgoliaItemsForBasketEventsGA4,
  getUnavailableStatus
} from './utils'
import {useRapidDeliveryStore} from '../store/hooks/useStore'

export const useSelectAlternativesTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const sendSelectAlternatives = (alternativesTrackingData) => {
    const {
      unavailableProduct,
      storeId,
      alternativesPath,
      modalDismissed,
      dismissalMethod,
      itemLocation
    } = alternativesTrackingData
    const unavailableStatus =
      getUnavailableStatus(unavailableProduct, isRapidDeliveryBooked) ?? 'unavailable_out_of_stock'

    const eventAttributes = {
      item_id: unavailableProduct?.id,
      item_name: unavailableProduct?.name,
      unavailable_status: unavailableStatus,
      store_id: storeId
    }
    if (dismissalMethod) {
      eventAttributes.dismissal_method = dismissalMethod
    } else {
      eventAttributes.alternatives_url = alternativesPath
    }
    setTimeout(() => {
      try {
        const eventData = {
          event: modalDismissed ? 'dismiss_alternatives' : 'select_alternatives',
          ecommerce: {
            item_location: itemLocation,
            items: [eventAttributes]
          }
        }
        sendDataToGTM(eventData)
      } catch (error) {
        console.error('useViewAlternativesTracking error within setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendSelectAlternatives
}

export const useAddToCartTracking = (basket) => {
  const sendDataToGTM = useGTMDispatch()
  const sendDataToDataCloud = useDataCloudDispatch()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const sendAddToCartData = (addToCartData) => {
    const {product, cartLocation, viewingCategoryId, searchQuery, hitItemIndex, value, mealDealId} =
      addToCartData
    setTimeout(() => {
      try {
        const transformAlgoliaData = {
          items: mealDealId != undefined && Array.isArray(product) ? product : [product],
          cartLocation,
          viewingCategoryId,
          searchQuery,
          hitItemIndex,
          isRapidDeliveryBooked
        }
        const _items = transformAlgoliaItemsForBasketEventsGA4(transformAlgoliaData)

        const eventData = {
          event: 'add_to_cart',
          ecommerce: {
            currency: 'GBP',
            value: value || _items[0].price * _items[0].quantity,
            item_location: cartLocation,
            coupon: basket?.couponItems?.[0]?.code ?? '',
            items: _items,
            meal_deal_id: mealDealId
          }
        }
        sendDataToGTM(eventData)
        sendDataToDataCloud(eventData)
      } catch (error) {
        console.error('useAddToCartTracking error within setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendAddToCartData
}

const withRemovedQuantity = (eventData, quantity) => {
  const [item, ...rest] = eventData.ecommerce.items
  return {
    ...eventData,
    ecommerce: {
      ...eventData.ecommerce,
      value: item.price * quantity,
      items: [{...item, quantity}, ...rest]
    }
  }
}

export const useRemoveFromCartTracking = () => {
  const sendDataToGTM = useGTMDispatch()
  const sendDataToDataCloud = useDataCloudDispatch()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  const sendRemoveFromCartData = (removeFromCartObject) => {
    const {product, cartLocation, viewingCategoryId, searchQuery, hitItemIndex, quantity, value, mealDealId} =
      removeFromCartObject
    setTimeout(() => {
      try {
        const transformAlgoliaData = {
          items: mealDealId != undefined && Array.isArray(product) ? product : [product],
          cartLocation,
          viewingCategoryId,
          searchQuery,
          hitItemIndex,
          isRapidDeliveryBooked
        }
        const _items = transformAlgoliaItemsForBasketEventsGA4(transformAlgoliaData)

        const eventData = {
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'GBP',
            value: value || _items[0].price * _items[0].quantity,
            item_location: cartLocation,
            items: _items,
            meal_deal_id: mealDealId
          }
        }
        sendDataToGTM(eventData)
        // GA4 keeps quantity 1; Data Cloud gets the number of units actually removed
        sendDataToDataCloud(quantity > 0 ? withRemovedQuantity(eventData, quantity) : eventData)
      } catch (error) {
        console.error('useRemoveFromCartTracking error within setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendRemoveFromCartData
}

export const useBeginCheckoutTracking = (productsById) => {
  const sendDataToGTM = useGTMDispatch()
  const sendDataToDataCloud = useDataCloudDispatch()
  const sendBeginCheckout = (basket, items) => {
    if (!basket || !items) return
    setTimeout(() => {
      try {
        const itemsData = transformSalesforceBasketItemsForGA4(items, productsById)
        const eventData = {
          event: 'begin_checkout',
          ecommerce: {
            transaction_id: basket.basketId,
            currency: 'GBP',
            value: basket.orderTotal,
            coupon: basket.couponItems?.[0]?.code ?? '',
            items: itemsData
          }
        }
        if (Object.keys(eventData.ecommerce.items).length > 0) {
          sendDataToGTM(eventData)
          sendDataToDataCloud(eventData)
        }
      } catch (error) {
        console.error('useBeginCheckoutTracking error within the setTimeout: ' + error.message)
      }
    }, 0)
  }
  return sendBeginCheckout
}
